import { onBeforeUnmount, onMounted, ref, watch, type Ref } from "vue";

/**
 * Sizes a frame to the shape of the media inside it, and holds it to the room the screen has left,
 * so a self-view shows the whole of what the camera is recording without pushing the controls
 * beneath the fold.
 *
 * The shape is carried as bottom padding, the one way to hold a ratio at this project's Safari 13
 * floor — the `aspect-ratio` property arrived in Safari 15. The frame keeps whatever shape its
 * classes give it until the media reports a size of its own.
 *
 * @param panel  the dialog panel the frame sits in, and what the room left on screen is measured
 *               against
 */
export function useMediaFrame(panel: Ref<HTMLElement | null>) {
    // Attach to a box around the aspect box, never to the aspect box itself: the padding holding
    // the ratio is a percentage of its *parent's* width, so capping the padded element's own width
    // would leave its height exactly where it was.
    const frame = ref<HTMLElement | null>(null);
    const frameShape = ref<{ paddingBottom?: string }>({});
    const frameCap = ref<{ maxWidth?: string }>({});

    let mediaHeightPerWidth = 0;

    /**
     * Take the shape of the element the event came from.
     *
     * Wire it to `resize` as well as to `loadedmetadata`: a phone turned on its side with the
     * camera open swaps the stream's dimensions and announces it only the first way.
     */
    function fitTheFrameToTheMedia(event: Event): void {
        const media = event.target as HTMLVideoElement;

        if (media.videoWidth <= 0 || media.videoHeight <= 0) {
            return;
        }

        mediaHeightPerWidth = media.videoHeight / media.videoWidth;
        frameShape.value = { paddingBottom: `${mediaHeightPerWidth * 100}%` };

        holdTheFrameToTheScreen();
    }

    function resetTheFrame(): void {
        mediaHeightPerWidth = 0;
        frameShape.value = {};
        frameCap.value = {};
    }

    /**
     * Cap the frame at the width whose height is all the screen has left for it.
     *
     * What the heading, the controls and the padding take is measured out of the panel as it
     * stands, so a second call corrects the first rather than compounding it.
     */
    function holdTheFrameToTheScreen(): void {
        const panelElement = panel.value;
        const frameElement = frame.value;

        if (!panelElement || !frameElement || frameElement.offsetWidth <= 0) {
            return;
        }

        const heightPerWidth =
            mediaHeightPerWidth > 0
                ? mediaHeightPerWidth
                : frameElement.offsetHeight / frameElement.offsetWidth;

        if (heightPerWidth <= 0) {
            return;
        }

        const restOfThePanel =
            panelElement.offsetHeight - frameElement.offsetHeight;
        const room = roomForThePanel(panelElement) - restOfThePanel;
        const widestFrameThatFits = Math.max(room, 0) / heightPerWidth;

        frameCap.value = { maxWidth: `${widestFrameThatFits}px` };
    }

    /**
     * How tall the panel may be: the visible viewport, less the padding the dialog holds it away
     * from the screen edges by.
     *
     * Measured rather than declared in `vh` because on iOS Safari `100vh` is the viewport with the
     * URL bar hidden — taller than what the customer can see, which is the reason the frame needs
     * a cap at all. `visualViewport` is read optionally: it arrived after this project's browser
     * floor and nothing here is polyfilled.
     */
    function roomForThePanel(panelElement: HTMLElement): number {
        const dialog = panelElement.parentElement;
        const dialogStyle = dialog === null ? null : getComputedStyle(dialog);

        return (
            (window.visualViewport?.height ?? window.innerHeight) -
            parseFloat(dialogStyle?.paddingTop ?? "0") -
            parseFloat(dialogStyle?.paddingBottom ?? "0")
        );
    }

    // The panel arrives when the dialog opens; measured after the flush, once it has been laid out.
    watch(panel, holdTheFrameToTheScreen, { flush: "post" });

    // The cap depends on the screen as much as on the stream, and a screen that changes size — a
    // phone turned on its side, a window dragged smaller — is announced by nothing but the window.
    //
    // Both events, because they are not the same one: iOS Safari sliding its own bars in and out
    // changes what the customer can see, which is what the cap is measured against, without moving
    // the layout viewport `window` speaks for.
    onMounted(() => {
        window.addEventListener("resize", holdTheFrameToTheScreen);
        window.visualViewport?.addEventListener(
            "resize",
            holdTheFrameToTheScreen,
        );
    });
    onBeforeUnmount(() => {
        window.removeEventListener("resize", holdTheFrameToTheScreen);
        window.visualViewport?.removeEventListener(
            "resize",
            holdTheFrameToTheScreen,
        );
    });

    return {
        frame,
        frameShape,
        frameCap,
        fitTheFrameToTheMedia,
        resetTheFrame,
    };
}
